<template>
  <div class="flex w-full flex-col">
    <div class="mt-12 flex flex-col">
      <div
        class="mx-auto mb-1 text-xs font-bold uppercase tracking-wider text-white"
      >
        - Win Condition -
      </div>
      <div class="mx-auto text-2xl uppercase tracking-wider text-white">
        {{ winCondition }}
      </div>
    </div>

    <div
      ref="wrapper"
      class="z-10 mx-auto mb-8 flex h-full w-full flex-col flex-wrap items-start justify-center text-white"
      :style="wrapperStyles"
    >
      <!-- Players -->
      <div class="mx-auto mb-4">
        <div
          v-for="player in playersScore"
          :key="player.player"
          class="mb-1 flex items-center px-2 py-1"
          :class="[
            {
              'rounded bg-black bg-opacity-20': player.player === activePlayer,
            },
            { 'opacity-50': player.player !== activePlayer },
          ]"
        >
          <!-- <div class="w-8">
            <IconArrow v-if="player.player === activePlayer" colour="#fff" />
          </div> -->
          <div
            class="flex h-6 w-6 select-none items-center justify-center overflow-hidden rounded-full p-1 text-xs"
            :class="[
              { 'bg-red-400': player.player === 1 },
              { 'bg-blue-400': player.player === 2 },
              { 'bg-green-400': player.player === 3 },
              { 'bg-yellow-400': player.player === 4 },
              { 'bg-purple-400': player.player === 5 },
            ]"
          ></div>
          <div class="ml-2">Player {{ player.player }}</div>
          <div
            v-if="winCondition === 'Highest Score'"
            class="ml-4 rounded-full bg-white px-3 py-1 text-xs uppercase tracking-wide text-gray-900"
          >
            {{ player.score }}
          </div>
        </div>
      </div>

      <!-- Win screen -->
      <transition name="fade">
        <div
          v-if="isActiveWinScreen && winner.length === 1"
          class="fixed bottom-0 left-0 right-0 top-0 z-50 bg-black bg-opacity-60"
        >
          <div
            class="winner-text"
            :class="[
              { 'text-red-400': winner[0] === 1 },
              { 'text-blue-400': winner[0] === 2 },
              { 'text-green-400': winner[0] === 3 },
              { 'text-yellow-400': winner[0] === 4 },
              { 'text-purple-400': winner[0] === 5 },
            ]"
          >
            Player {{ winner[0] }}<br />Wins!
          </div>
        </div>
      </transition>

      <!-- Draw -->
      <transition name="fade">
        <div
          v-if="isActiveWinScreen && winner.length === 0"
          class="fixed bottom-0 left-0 right-0 top-0 z-50 bg-black bg-opacity-60"
        >
          <div class="winner-text text-gray-900">DRAW!</div>
        </div>
      </transition>

      <!-- Board -->
      <div class="board" :style="boardStyles" ref="board">
        <template v-for="(row, rowIndex) in board">
          <div
            v-for="(slot, colIndex) in row"
            :key="'row' + rowIndex + 'col' + colIndex"
            :ref="'row' + rowIndex + 'col' + colIndex"
            class="tile transition-all duration-300"
            :class="{
              'opacity-50 saturate-0 filter':
                !winner.includes(0) &&
                !winner.includes(board[rowIndex][colIndex]),
            }"
            @click="squareSelected(rowIndex, colIndex)"
          >
            <div
              class="flex h-full select-none items-center justify-center overflow-hidden rounded"
              :class="[
                { 'bg-red-400': board[rowIndex][colIndex] === 1 },
                { 'bg-blue-400': board[rowIndex][colIndex] === 2 },
                { 'bg-green-400': board[rowIndex][colIndex] === 3 },
                { 'bg-yellow-300': board[rowIndex][colIndex] === 4 },
                { 'bg-purple-400': board[rowIndex][colIndex] === 5 },
              ]"
            >
              <IconX v-if="board[rowIndex][colIndex] === 1" colour="#fff" />
              <IconCircle
                v-if="board[rowIndex][colIndex] === 2"
                colour="#fff"
              />
              <IconSquare
                v-if="board[rowIndex][colIndex] === 3"
                colour="#fff"
              />
              <IconTriangle
                v-if="board[rowIndex][colIndex] === 4"
                colour="#fff"
              />
              <IconStar v-if="board[rowIndex][colIndex] === 5" colour="#fff" />
            </div>
          </div>
        </template>
      </div>

      <div class="mt-8 flex h-12 w-full justify-center">
        <transition name="fade">
          <div v-if="isGameover" class="mx-2">
            <button class="btn" @click="reset">Play again?</button>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import IconArrow from '@/components/IconArrow';
import IconCircle from '@/components/IconCircle';
import IconStar from '@/components/IconStar';
import IconSquare from '@/components/IconSquare';
import IconTriangle from '@/components/IconTriangle';
import IconX from '@/components/IconX';

export default {
  props: ['rows', 'cols', 'players', 'winCondition', 'isActive'],

  components: {
    IconArrow,
    IconCircle,
    IconStar,
    IconSquare,
    IconTriangle,
    IconX,
  },

  data() {
    return {
      scale: 1,
      board: [],
      lines: [],
      round: 0,
      activePlayer: 1,
      scores: [],
      winner: [0],
      isGameover: false,
      isActiveWinScreen: false,
      lockGame: false,
    };
  },

  computed: {
    wrapperStyles() {
      return `
        max-width: ${80 * this.cols + (8 * this.cols - 1)}px;
      `;
    },

    boardStyles() {
      return `
        grid-template-columns: repeat(${this.cols}, 1fr);
        grid-template-rows: repeat(${this.rows}, 1fr);
        font-size: ${this.scale}em;
      `;
    },

    playersScore() {
      let scores = [];

      for (let index = 1; index <= this.players; index++) {
        const playerScores = this.scores.filter(
          (score) => score.player === index
        );

        let theScore = 0;
        if (playerScores) {
          const reducer = (sum, val) => sum + val.score;
          theScore = playerScores.reduce(reducer, 0);
        }

        scores.push({
          player: index,
          score: theScore,
        });
      }

      return scores;
    },

    maxRounds() {
      return this.rows * this.cols;
    },
  },

  mounted() {
    this.createBoard();
  },

  methods: {
    createBoard() {
      let rows = [];
      for (let indexR = 0; indexR < this.rows; indexR++) {
        let cols = [];
        for (let indexC = 0; indexC < this.cols; indexC++) {
          cols.push(0);
        }

        rows.push(cols);
      }

      this.board = rows;
    },

    squareSelected(row, col) {
      if (!this.isActive || this.lockGame) {
        return;
      }

      if (!this.winner.includes(0)) {
        return;
      }

      if (this.board[row][col] !== 0) {
        return;
      }

      this.board[row][col] = this.activePlayer;
      this.round++;

      this.checkForWinner();

      if (this.winner.includes(0)) {
        this.activePlayer++;

        if (this.activePlayer > this.players) {
          this.activePlayer = 1;
        }
      }
    },

    checkForWinner() {
      this.lines = [];
      this.scores = [];

      this.storeCols();
      this.storeRows();
      this.storeDiagsRight();
      this.storeDiagsLeft();

      if (this.winCondition === 'Highest Score') {
        this.lines.forEach((line) => {
          this.calculateScores(line);
        });

        if (this.round === this.rows * this.cols) {
          let highestScores = [];

          this.playersScore.forEach((player) => {
            const target = Math.max(
              ...highestScores.map((player) => player.score)
            );

            if (player.score === target || !highestScores.length) {
              highestScores.push(player);
            }

            if (player.score > target) {
              highestScores = [player];
            }
          });

          this.lockGame = true;

          setTimeout(() => {
            this.winner = [];
            this.winner = highestScores.map((player) => player.player);
          }, 1000);

          setTimeout(() => {
            this.isActiveWinScreen = true;
          }, 2000);

          setTimeout(() => {
            this.isGameover = true;
            this.isActiveWinScreen = false;
          }, 5000);
        }
      }

      if (this.winCondition === 'First to 3') {
        this.lines.forEach((line) => {
          this.findThree(line);
        });

        if (this.maxRounds === this.round && !this.isGameover) {
          this.lockGame = true;

          setTimeout(() => {
            this.winner = [];
          }, 1000);
          setTimeout(() => {
            this.isActiveWinScreen = true;
          }, 2000);

          setTimeout(() => {
            this.isGameover = true;
            this.isActiveWinScreen = false;
          }, 5000);
        }
      }
    },

    storeCols() {
      let colLines = [];

      for (let col = 0; col < this.cols; col++) {
        let spaces = [];

        for (let row = 0; row < this.rows; row++) {
          spaces.push({
            pos: [row, col],
            value: this.board[row][col],
          });
        }

        this.lines.push(spaces);
      }

      return colLines;
    },

    storeRows() {
      let rowLines = [];

      for (let row = 0; row < this.rows; row++) {
        let spaces = [];

        for (let col = 0; col < this.cols; col++) {
          spaces.push({
            pos: [row, col],
            value: this.board[row][col],
          });
        }

        this.lines.push(spaces);
      }

      return rowLines;
    },

    storeDiagsRight() {
      let diagsRightLines = [];
      let rowIndex = 0;

      for (let col = 0; col < this.board[0].length; col++) {
        let spaces = [];
        let colIndex = col;

        for (let row = 0; row < this.board.length; row++) {
          if (colIndex < this.board[0].length) {
            spaces.push({
              pos: [row, colIndex],
              value: this.board[row][colIndex],
            });
          }

          colIndex++;
        }

        if (spaces.length >= 3) {
          this.lines.push(spaces);
        }
      }

      for (let row = 1; row < this.board[0].length; row++) {
        let spaces = [];
        rowIndex = row;

        for (let col = 0; col < this.board.length; col++) {
          if (rowIndex < this.board.length) {
            spaces.push({
              pos: [rowIndex, col],
              value: this.board[rowIndex][col],
            });
          }

          rowIndex++;
        }

        if (spaces.length >= 3) {
          this.lines.push(spaces);
        }
      }

      return diagsRightLines;
    },

    storeDiagsLeft() {
      let diagsLeftLines = [];
      let rowIndex = 0;

      for (let col = this.board[0].length - 1; col >= 0; col--) {
        let spaces = [];
        let colIndex = col;

        for (let row = 0; row < this.board.length; row++) {
          if (colIndex >= 0) {
            spaces.push({
              pos: [row, colIndex],
              value: this.board[row][colIndex],
            });
          }

          colIndex--;
        }

        if (spaces.length >= 3) {
          this.lines.push(spaces);
        }
      }

      for (let row = 1; row < this.board[0].length; row++) {
        let spaces = [];
        rowIndex = row;

        for (let col = this.board.length - 1; col >= 0; col--) {
          if (rowIndex < this.board.length) {
            spaces.push({
              pos: [rowIndex, col],
              value: this.board[rowIndex][col],
            });
          }

          rowIndex++;
        }

        if (spaces.length >= 3) {
          this.lines.push(spaces);
        }
      }

      return diagsLeftLines;
    },

    calculateScores(line) {
      let currentPlayer = 0;
      let currentRowCount = 0;

      let lineScores = [];

      for (let index = 0; index < line.length; index++) {
        const element = line[index].value;

        if (currentPlayer === element) {
          currentRowCount++;
        } else {
          let rowScore = 0;

          switch (currentRowCount) {
            case 3:
              rowScore = 1;
              break;

            case 4:
              rowScore = 3;
              break;

            case 5:
              rowScore = 6;
              break;

            case 6:
              rowScore = 10;
              break;

            case 7:
              rowScore = 15;
              break;

            case 8:
              rowScore = 21;
              break;

            default:
              break;
          }

          let lineScore = {
            player: currentPlayer,
            score: rowScore,
          };

          if (currentPlayer !== 0) {
            lineScores.push(lineScore);
          }

          currentPlayer = element;
          currentRowCount = 1;
        }
      }

      let rowScore = 0;

      switch (currentRowCount) {
        case 3:
          rowScore = 1;
          break;

        case 4:
          rowScore = 3;
          break;

        case 5:
          rowScore = 6;
          break;

        case 6:
          rowScore = 10;
          break;

        case 7:
          rowScore = 15;
          break;

        case 8:
          rowScore = 21;
          break;

        default:
          break;
      }

      let lineScore = {
        player: currentPlayer,
        score: rowScore,
      };

      if (currentPlayer !== 0) {
        lineScores.push(lineScore);
      }

      this.scores.push(...lineScores);
    },

    findThree(line) {
      let currentPlayer = 0;
      let currentRowCount = 0;

      for (let index = 0; index < line.length; index++) {
        const element = line[index].value;

        if (currentPlayer === element) {
          currentRowCount++;
        } else {
          currentPlayer = element;
          currentRowCount = 1;
        }

        if (element !== 0 && currentRowCount === 3) {
          this.lockGame = true;

          setTimeout(() => {
            this.winner = [];
            this.winner.push(element);
          }, 1000);

          setTimeout(() => {
            this.isActiveWinScreen = true;
          }, 2000);

          setTimeout(() => {
            this.isGameover = true;
            this.isActiveWinScreen = false;
          }, 5000);
        }
      }
    },

    reset() {
      this.board = [];
      this.lines = [];
      this.round = 0;
      this.activePlayer = 1;
      this.scores = [];
      this.winner = [0];
      this.isGameover = false;
      this.lockGame = false;
      this.createBoard();
    },
  },
};
</script>

<style lang="scss" scoped>
$tilesize: 5em;
$tilegap: 0.5em;

.tile {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 0.25em;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.2);
  box-sizing: border-box;
  cursor: grab;
  position: relative;
}

.board {
  aspect-ratio: 1/1;
  display: grid;
  grid-column-gap: $tilegap;
  grid-row-gap: $tilegap;
  position: relative;
  width: 100%;
}

.winner-text {
  @apply pointer-events-none fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform text-center font-bungee text-7xl;
  text-shadow:
    0.01em 0.01em 0 #fff,
    0.02em 0.02em 0 #fff,
    0.03em 0.03em 0 #fff,
    0.04em 0.04em 0 #fff,
    0.05em 0.05em 0.1em #fff;
}
</style>



// WEBPACK FOOTER //
// src/components/VBoard.vue